import React from 'react';
import { useSelector } from 'react-redux';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid, 
  Card, 
  CardContent,
  Alert,
  Chip,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Skeleton,
  Stack,
  IconButton,
  Tooltip,
  Container,
  TextField,
  InputAdornment,
  Button,
  Fade,
  Collapse,
  alpha,
  MenuItem,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { 
  Refresh as RefreshIcon,
  AccountBalanceWallet as AccountBalanceWalletIcon,
  Receipt as ReceiptIcon,
  People as PeopleIcon,
  TrendingDown as TrendingDownIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Phone as PhoneIcon,
  FilterList as FilterListIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  HourglassEmpty as HourglassEmptyIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { useGetWithdrawalsQuery, useLazyGetWithdrawalsQuery } from '../../store/api/dashboardApi';
import { formatCurrency } from '../../utils/formatters';

const Withdrawals = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  
  // Search and filter state
  const [searchInput, setSearchInput] = React.useState('');
  const [searchQuery, setSearchQuery] = React.useState('');
  const [searchMode, setSearchMode] = React.useState(false);
  const [statusFilter, setStatusFilter] = React.useState('');
  const [showFilters, setShowFilters] = React.useState(false);
  
  // Get filters from Redux (including date range)
  const dashboardFilters = useSelector((state) => state.dashboard.filters);
  
  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: isMobile ? 10 : 25,
  });

  // Main query - skipped during search mode, uses dashboard filters including date range
  const { 
    data: withdrawalsData, 
    isLoading, 
    error,
    refetch,
    isFetching 
  } = useGetWithdrawalsQuery(dashboardFilters, {
    skip: searchMode,
  });

  // Lazy search query
  const [
    searchWithdrawals, 
    { 
      data: searchResults, 
      isLoading: isSearching, 
      error: searchError 
    }
  ] = useLazyGetWithdrawalsQuery();

  React.useEffect(() => {
    setPaginationModel(prev => ({
      ...prev,
      pageSize: isMobile ? 10 : 25
    }));
  }, [isMobile]);

  // Handle search input change with debounce
  const searchTimeoutRef = React.useRef();

  const handleSearchInputChange = (event) => {
    const value = event.target.value;
    setSearchInput(value);
    
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      const trimmedQuery = value.trim();
      if (trimmedQuery) {
        performSearch(trimmedQuery, statusFilter);
      } else {
        clearSearch();
      }
    }, 500); // 500ms debounce
  };

  const handleStatusChange = (event) => {
    const value = event.target.value;
    setStatusFilter(value);
    
    if (searchInput.trim() || value) {
      performSearch(searchInput.trim(), value);
    }
  };

  const performSearch = (query, status) => {
    const searchParams = {};
    
    if (query) {
      searchParams.search = query;
    }
    
    if (status) {
      searchParams.status = status;
    }
    
    setSearchQuery(query);
    setSearchMode(true);
    searchWithdrawals(searchParams);
  };

  const clearSearch = () => {
    setSearchInput('');
    setSearchQuery('');
    setStatusFilter('');
    setSearchMode(false);
    setShowFilters(false);
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
  };

  // Get display data based on search mode - memoized to prevent unnecessary recalculations
  const displayData = React.useMemo(() => {
    if (searchMode) {
      return searchResults || {
        summary: { recordsReturned: 0, totalAmount: 0 },
        withdrawals: []
      };
    }
    return withdrawalsData;
  }, [searchMode, searchResults, withdrawalsData]);

  const summary = React.useMemo(() => displayData?.summary || {}, [displayData]);
  const withdrawals = React.useMemo(() => displayData?.withdrawals || [], [displayData]);

  const rows = React.useMemo(() => {
    return withdrawals.map((withdrawal, index) => ({
      id: withdrawal.payout_id || withdrawal.transaction_id || withdrawal.session_id || `withdrawal-${index}`,
      ...withdrawal,
      createdAt: withdrawal.created_at ? new Date(withdrawal.created_at) : null,
      updatedAt: withdrawal.updated_at ? new Date(withdrawal.updated_at) : null,
      transactionDate: withdrawal.transaction_date ? new Date(withdrawal.transaction_date) : null,
      // Ensure status is uppercase for consistent display
      status: (withdrawal.status || 'UNKNOWN').toUpperCase(),
      // Use displayId from the API or construct one
      displayId: withdrawal.displayId || withdrawal.payout_id || withdrawal.transaction_id || '',
    }));
  }, [withdrawals]);

  // Status helper functions
  const getStatusColor = (status) => {
    const statusUpper = status?.toUpperCase();
    switch (statusUpper) {
      case 'SUCCESS':
      case 'COMPLETED':
        return 'success';
      case 'PENDING':
      case 'PROCESSING':
        return 'warning';
      case 'ACCEPTED':
      case 'APPROVED':
        return 'info';
      case 'FAILED':
      case 'ERROR':
        return 'error';
      case 'CANCELLED':
      case 'REJECTED':
        return 'default';
      default:
        return 'primary';
    }
  };

  const getStatusIcon = (status) => {
    const statusUpper = status?.toUpperCase();
    switch (statusUpper) {
      case 'SUCCESS':
      case 'COMPLETED':
        return <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />;
      case 'ACCEPTED':
      case 'APPROVED':
        return <CheckCircleIcon sx={{ fontSize: 16, color: 'info.main' }} />;
      case 'PENDING':
      case 'PROCESSING':
        return <HourglassEmptyIcon sx={{ fontSize: 16, color: 'warning.main' }} />;
      case 'FAILED':
      case 'ERROR':
        return <ErrorIcon sx={{ fontSize: 16, color: 'error.main' }} />;
      case 'CANCELLED':
      case 'REJECTED':
        return <CancelIcon sx={{ fontSize: 16, color: 'text.secondary' }} />;
      default:
        return <HourglassEmptyIcon sx={{ fontSize: 16, color: 'primary.main' }} />;
    }
  };

  const getColumns = React.useCallback(() => {
    const baseColumns = [
      { 
        field: 'phone', 
        headerName: 'Phone', 
        width: 140,
        minWidth: 120,
        flex: 1,
        renderCell: (params) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PhoneIcon sx={{ fontSize: 16, color: 'primary.main' }} />
            <Typography 
              variant="body2" 
              sx={{ 
                fontFamily: 'monospace',
                fontSize: { xs: '0.75rem', md: '0.875rem' },
                fontWeight: searchMode ? 'bold' : 'normal',
                color: searchMode ? 'primary.main' : 'inherit'
              }}
            >
              {params.value}
            </Typography>
          </Box>
        )
      },
      { 
        field: 'amount', 
        headerName: 'Amount', 
        width: isMobile ? 100 : 120, 
        type: 'number',
        minWidth: 90,
        valueFormatter: (value) => formatCurrency(value ?? 0),
        renderCell: (params) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <TrendingDownIcon sx={{ fontSize: 16, color: 'error.main' }} />
            <Typography 
              variant="body2" 
              color="error.main"
              sx={{ 
                fontWeight: 'medium',
                fontSize: { xs: '0.75rem', md: '0.875rem' }
              }}
            >
              {formatCurrency(params.value ?? 0)}
            </Typography>
          </Box>
        )
      },
      { 
        field: 'status', 
        headerName: 'Status', 
        width: isMobile ? 100 : 120, 
        minWidth: 90,
        renderCell: (params) => (
          <Chip 
            label={params.value || 'UNKNOWN'}
            color={getStatusColor(params.value)}
            size="small"
            variant="outlined"
            icon={getStatusIcon(params.value)}
            sx={{ fontSize: { xs: '0.7rem', md: '0.75rem' } }}
          />
        )
      },
      { 
        field: 'provider', 
        headerName: 'Provider', 
        width: isMobile ? 100 : 120, 
        minWidth: 90,
        renderCell: (params) => (
          <Chip 
            label={params.value || 'N/A'}
            color="primary"
            size="small"
            variant="outlined"
            sx={{ fontSize: { xs: '0.7rem', md: '0.75rem' } }}
          />
        )
      }
    ];

    if (!isMobile) {
      baseColumns.push(
        { 
          field: 'displayId', 
          headerName: 'Payout ID', 
          width: 160,
          renderCell: (params) => (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ReceiptIcon sx={{ fontSize: 16, color: 'info.main' }} />
              <Typography 
                variant="body2" 
                sx={{ 
                  fontFamily: 'monospace',
                  fontSize: '0.75rem',
                  color: 'info.main'
                }}
              >
                {params.value ? `${params.value.substring(0, 12)}...` : 'N/A'}
              </Typography>
            </Box>
          )
        },
        { 
          field: 'createdAt', 
          headerName: 'Date', 
          width: 120, 
          type: 'dateTime',
          valueFormatter: (value) => value ? new Date(value).toLocaleDateString() : 'N/A'
        }
      );
    }

    if (!isTablet) {
      baseColumns.push(
        { 
          field: 'payout_type', 
          headerName: 'Type', 
          width: 100,
          renderCell: (params) => (
            <Chip 
              label={params.value || 'N/A'}
              color="secondary"
              size="small"
              variant="outlined"
              sx={{ fontSize: '0.7rem' }}
            />
          )
        },
        { 
          field: 'currency', 
          headerName: 'Currency', 
          width: 80,
          renderCell: (params) => (
            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
              {params.value || 'ZMW'}
            </Typography>
          )
        },
        { 
          field: 'operator', 
          headerName: 'Operator', 
          width: 100,
          renderCell: (params) => (
            <Typography variant="body2" color="text.secondary">
              {params.value || '-'}
            </Typography>
          )
        }
      );
    }

    return baseColumns;
  }, [isMobile, isTablet, searchMode]);

  // Get unique statuses for filter
  const statuses = React.useMemo(() => {
    const statusSet = new Set();
    withdrawals.forEach(withdrawal => {
      if (withdrawal.status) {
        statusSet.add(withdrawal.status);
      }
    });
    return Array.from(statusSet).sort();
  }, [withdrawals]);

  // Calculate status breakdown (prefer API values, fallback to local calculation)
  const statusBreakdown = React.useMemo(() => {
    // Use API-provided breakdown if available
    if (summary.statusBreakdown && Object.keys(summary.statusBreakdown).length > 0) {
      // Normalize the breakdown to uppercase keys for consistent display
      const breakdown = {};
      Object.entries(summary.statusBreakdown).forEach(([key, value]) => {
        const normalizedKey = key.toUpperCase();
        breakdown[normalizedKey] = (breakdown[normalizedKey] || 0) + value;
      });
      return breakdown;
    }
    // Fallback to local calculation
    const breakdown = {};
    withdrawals.forEach(withdrawal => {
      const status = (withdrawal.status || 'UNKNOWN').toUpperCase();
      breakdown[status] = (breakdown[status] || 0) + 1;
    });
    return breakdown;
  }, [withdrawals, summary]);

  const summaryCards = [
    { 
      title: 'Total Withdrawals', 
      value: summary.recordsReturned?.toLocaleString() || '0', 
      color: 'primary.main', 
      icon: <ReceiptIcon />, 
      bgColor: theme.palette.primary.light 
    },
    { 
      title: 'Total Requested', 
      value: formatCurrency(summary.totalAmount ?? 0), 
      subtitle: `Avg: ${formatCurrency(summary.averageWithdrawal ?? 0)}`,
      color: 'error.main', 
      icon: <AccountBalanceWalletIcon />, 
      bgColor: theme.palette.error.light 
    },
    { 
      title: 'Completed Payouts', 
      value: formatCurrency(summary.completedAmount ?? 0), 
      subtitle: `${summary.successRate ?? 0}% success rate`,
      color: 'info.main', 
      icon: <CheckCircleIcon />, 
      bgColor: theme.palette.info.light 
    },
    { 
      title: 'Unique Users', 
      value: (summary.uniqueUsers || new Set(withdrawals.map(w => w.phone)).size).toLocaleString() || '0', 
      subtitle: `Pending: ${formatCurrency(summary.pendingAmount ?? 0)}`,
      color: 'warning.main', 
      icon: <PeopleIcon />, 
      bgColor: theme.palette.warning.light 
    }
  ];

  const LoadingSkeleton = () => (
    <Box>
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {[1, 2, 3, 4].map((index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card><CardContent><Skeleton variant="text" width="60%" height={32} /><Skeleton variant="text" width="40%" /></CardContent></Card>
          </Grid>
        ))}
      </Grid>
      <Paper sx={{ height: 400 }}><Skeleton variant="rectangular" height="100%" /></Paper>
    </Box>
  );

  if (isLoading && !searchMode) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ py: 2 }}>
          <Skeleton variant="text" width="200px" height={40} sx={{ mb: 1 }} />
          <Skeleton variant="text" width="300px" height={24} sx={{ mb: 3 }} />
          <LoadingSkeleton />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 2 }}>
        {/* Header Section */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: { xs: 'flex-start', md: 'center' },
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2,
          mb: 3 
        }}>
          <Box>
            <Typography 
              variant={isMobile ? 'h5' : 'h4'}
              component="h1" 
              sx={{ fontWeight: 'bold', mb: 1 }}
            >
              Withdrawals {searchMode && <Chip label="Search Results" color="primary" size="small" sx={{ ml: 1 }} />}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {searchMode ? `Search results for: ${searchQuery || 'filtered data'}` : 'All-time withdrawal transactions and status analysis'}
            </Typography>
          </Box>
          
          <Tooltip title="Refresh data">
            <IconButton 
              onClick={() => refetch()} 
              disabled={isFetching || isSearching} 
              color="primary" 
              sx={{ 
                bgcolor: 'primary.light', 
                '&:hover': { bgcolor: 'primary.main', color: 'white' } 
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Summary Cards */}
        <Grid container spacing={{ xs: 2, md: 3 }} sx={{ mb: 3 }}>
          {summaryCards.map((card, index) => (
            <Grid item xs={6} sm={6} md={3} key={index}>
              <Card sx={{ 
                height: '100%', 
                background: `linear-gradient(135deg, ${alpha(card.bgColor, 0.2)} 0%, ${alpha(card.bgColor, 0.1)} 100%)`, 
                border: `1px solid ${alpha(card.bgColor, 0.4)}`, 
                transition: 'transform 0.2s, box-shadow 0.2s', 
                '&:hover': { transform: 'translateY(-2px)', boxShadow: theme.shadows[8] } 
              }}>
                <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    <Box sx={{ color: card.color, display: 'flex', fontSize: { xs: '1.2rem', md: '1.5rem' } }}>
                      {card.icon}
                    </Box>
                    <Typography 
                      component="div" 
                      sx={{ 
                        color: card.color, 
                        fontWeight: 'bold', 
                        fontSize: { xs: '1rem', md: '1.25rem' } 
                      }}
                    >
                      {card.value}
                    </Typography>
                  </Stack>
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}
                  >
                    {card.title}
                  </Typography>
                  {card.subtitle && (
                    <Typography
                      variant="caption"
                      color="text.disabled"
                      sx={{ fontSize: { xs: '0.65rem', md: '0.75rem' }, display: 'block', mt: 0.5 }}
                    >
                      {card.subtitle}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Status Breakdown Cards */}
        {Object.keys(statusBreakdown).length > 0 && (
          <Grid container spacing={{ xs: 2, md: 3 }} sx={{ mb: 3 }}>
            {Object.entries(statusBreakdown).map(([status, count]) => (
              <Grid item xs={6} sm={4} md={2} key={status}>
                <Card sx={{ 
                  height: '100%',
                  border: `1px solid ${theme.palette[getStatusColor(status)]?.main}`,
                  borderRadius: 2
                }}>
                  <CardContent sx={{ p: 2, textAlign: 'center' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                      {getStatusIcon(status)}
                    </Box>
                    <Typography variant="h6" color={`${getStatusColor(status)}.main`}>
                      {count}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {status}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Search and Filter Section */}
        <Card sx={{ 
          mb: 3, 
          background: `linear-gradient(135deg, ${alpha(theme.palette.background.default, 0.6)} 0%, ${alpha(theme.palette.primary.light, 0.15)} 100%)`,
          border: '1px solid',
          borderColor: 'divider',
          transition: 'transform 0.2s, box-shadow 0.2s', 
          '&:hover': { transform: 'translateY(-1px)', boxShadow: theme.shadows[4] }
        }}>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Stack spacing={2}>
              {/* Search Bar */}
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
                <TextField
                  fullWidth
                  placeholder="Search by phone, receipt, or transaction ID..."
                  value={searchInput}
                  onChange={handleSearchInputChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: searchInput && (
                      <InputAdornment position="end">
                        <IconButton onClick={clearSearch} size="small">
                          <ClearIcon />
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: theme.palette.background.paper,
                    }
                  }}
                />
                
                <Button
                  variant="outlined"
                  startIcon={<FilterListIcon />}
                  onClick={() => setShowFilters(!showFilters)}
                  sx={{ minWidth: 'fit-content' }}
                >
                  Filters
                </Button>
                
                {isSearching && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 'fit-content' }}>
                    <CircularProgress size={20} />
                    <Typography variant="body2" color="text.secondary">Searching...</Typography>
                  </Box>
                )}
              </Stack>
              
              {/* Filters */}
              <Collapse in={showFilters}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ pt: 2 }}>
                  <FormControl sx={{ minWidth: 200 }}>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={statusFilter}
                      label="Status"
                      onChange={handleStatusChange}
                    >
                      <MenuItem value="">All Statuses</MenuItem>
                      {statuses.map((status) => (
                        <MenuItem key={status} value={status}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {getStatusIcon(status)}
                            {status}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>
              </Collapse>
            </Stack>
          </CardContent>
        </Card>

        {/* Search Results Alert */}
        {searchMode && withdrawals.length === 0 && (
          <Fade in>
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                No withdrawals found for the current search criteria.
              </Typography>
              <Button onClick={clearSearch} size="small" sx={{ mt: 1 }}>
                View All Withdrawals
              </Button>
            </Alert>
          </Fade>
        )}

        {searchMode && withdrawals.length > 0 && (
          <Fade in>
            <Alert severity="success" sx={{ mb: 3 }}>
              <Typography variant="body2">
                ✅ Found {withdrawals.length} withdrawal{withdrawals.length !== 1 ? 's' : ''}
              </Typography>
              <Button onClick={clearSearch} size="small" sx={{ mt: 1 }}>
                View All Withdrawals
              </Button>
            </Alert>
          </Fade>
        )}

        {/* Error Handling */}
        {(error || searchError) && (
          <Alert severity="error" sx={{ mb: 3 }}>
            Error loading withdrawal data: {error?.message || searchError?.message || 'Unknown error'}
          </Alert>
        )}

        {/* Data Grid Section */}
        <Paper sx={{ height: { xs: 500, md: 600 }, width: '100%', overflow: 'hidden' }}>
          <DataGrid
            rows={rows}
            columns={getColumns()}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={isMobile ? [10, 25] : [25, 50, 100]}
            rowCount={rows.length}
            loading={isLoading || isFetching || isSearching}
            disableRowSelectionOnClick
            sx={{
              '& .MuiDataGrid-root': { border: 'none' },
              '& .MuiDataGrid-cell': { borderBottom: '1px solid', borderBottomColor: 'divider' },
              '& .MuiDataGrid-columnHeaders': { 
                backgroundColor: alpha(theme.palette.primary.light, 0.1), 
                borderBottom: '2px solid', 
                borderBottomColor: 'divider' 
              },
              '& .MuiDataGrid-row:hover': { backgroundColor: 'action.hover' },
              '& .MuiDataGrid-row:nth-of-type(even)': { 
                backgroundColor: alpha(theme.palette.action.hover, 0.5) 
              },
              ...(isMobile && {
                '& .MuiDataGrid-columnHeader': { fontSize: '0.75rem', fontWeight: 'bold' },
                '& .MuiDataGrid-cell': { fontSize: '0.75rem', py: 0.5 },
              }),
            }}
            getRowId={(row) => row.id}
            density={isMobile ? 'compact' : 'standard'}
          />
        </Paper>

        {/* Data Info Footer */}
        <Box sx={{ 
          mt: 2, 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          flexDirection: { xs: 'column', md: 'row' }, 
          gap: 1 
        }}>
          <Typography variant="caption" color="text.secondary">
            {searchMode && <Chip label="Filtered" size="small" sx={{ mr: 1 }} />}
            Total Amount: {formatCurrency(summary.totalAmount ?? 0)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Showing {rows.length} withdrawal{rows.length !== 1 ? 's' : ''} 
            {searchMode ? ` (Search Results)` : ' (All-Time Data)'}
          </Typography>
        </Box>

        {/* No withdrawals found */}
        {!searchMode && !isLoading && withdrawals.length === 0 && (
          <Alert severity="warning" sx={{ mt: 3 }}>
            No withdrawal data available. This could be due to no transactions or database connectivity issues.
          </Alert>
        )}
      </Box>
    </Container>
  );
};

export default Withdrawals;

// // src/pages/Withdrawals/Withdrawals.jsx
// import React from 'react';
// import { Box, Typography, Paper } from '@mui/material';
// import { DataGrid } from '@mui/x-data-grid';

// const Withdrawals = () => {
//   const [loading, setLoading] = React.useState(false);
//   const [rows, setRows] = React.useState([]);
//   const [rowCount, setRowCount] = React.useState(0);
//   const [paginationModel, setPaginationModel] = React.useState({
//     page: 0,
//     pageSize: 25,
//   });

//   const columns = [
//     { field: 'id', headerName: 'ID', width: 90 },
//     { field: 'customerPhone', headerName: 'Customer', width: 150 },
//     { field: 'amount', headerName: 'Amount', width: 120, type: 'number' },
//     { field: 'method', headerName: 'Method', width: 150 },
//     { field: 'status', headerName: 'Status', width: 120 },
//     { field: 'createdAt', headerName: 'Date', width: 150, type: 'dateTime' },
//   ];

//   return (
//     <Box>
//       <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
//         Withdrawals
//       </Typography>
//       <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
//         Process customer withdrawal requests
//       </Typography>

//       <Paper sx={{ height: 600, width: '100%' }}>
//         <DataGrid
//           rows={rows}
//           columns={columns}
//           paginationModel={paginationModel}
//           onPaginationModelChange={setPaginationModel}
//           pageSizeOptions={[25, 50, 100]}
//           rowCount={rowCount}
//           loading={loading}
//           paginationMode="server"
//           sortingMode="server"
//           filterMode="server"
//           disableRowSelectionOnClick
//         />
//       </Paper>
//     </Box>
//   );
// };

// export default Withdrawals;